import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Table, Alert, Modal, Form, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWrench, 
  faEye,
  faSignOutAlt,
  faHome,
  faCalendarAlt,
  faFilter,
  faSearch,
  faUser,
  faPhone,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { maintenanceService } from '../../services/maintenanceService';
import { userService } from '../../services/userService';

const MaintenanceRequests = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  const [requests, setRequests] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState({ status: '', notes: '' });

  const maintenanceTypes = {
    plumbing: 'Plumbing Issues',
    electrical: 'Electrical Issues',
    hvac: 'HVAC/Heating & Cooling',
    appliance: 'Appliance Repair',
    structural: 'Structural Issues',
    pest_control: 'Pest Control',
    cleaning: 'Cleaning Services',
    landscaping: 'Landscaping',
    security: 'Security Issues',
    other: 'Other'
  };

  const priorityColors = {
    low: 'success',
    medium: 'warning',
    high: 'danger',
    urgent: 'danger'
  };

  const statusColors = {
    pending: 'warning',
    in_progress: 'info',
    completed: 'success',
    cancelled: 'secondary'
  };

  const statusLabels = {
    pending: 'Pending',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled'
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const profile = await userService.getUserProfile();
      setUserProfile(profile);
      
      const [requestsData, statsData] = await Promise.all([
        maintenanceService.getMaintenanceRequestsByCompany(profile.companyId),
        maintenanceService.getMaintenanceStats(profile.companyId)
      ]);
      
      setRequests(requestsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load maintenance requests');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleStatusUpdate = (request) => {
    setSelectedRequest(request);
    setStatusUpdate({ status: request.status, notes: request.managerNotes || '' });
    setShowStatusModal(true);
  };

  const confirmStatusUpdate = async () => {
    try {
      await maintenanceService.updateMaintenanceRequestStatus(
        selectedRequest.id, 
        statusUpdate.status, 
        statusUpdate.notes
      );
      
      // Update local state
      setRequests(requests.map(r => 
        r.id === selectedRequest.id 
          ? { ...r, status: statusUpdate.status, managerNotes: statusUpdate.notes }
          : r
      ));
      
      // Refresh stats
      const newStats = await maintenanceService.getMaintenanceStats(userProfile.companyId);
      setStats(newStats);
      
      setShowStatusModal(false);
      setSelectedRequest(null);
      setStatusUpdate({ status: '', notes: '' });
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update request status');
    }
  };

  const filteredRequests = requests.filter(request => {
    const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
    const matchesSearch = searchTerm === '' || 
      request.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.requestId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.tenantName.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Maintenance Requests</h2>
          <p className="text-muted mb-0">Manage all maintenance requests</p>
        </div>
        <div className="d-flex gap-2">
          <Button as={Link} to="/dashboard" variant="outline-secondary">
            <FontAwesomeIcon icon={faHome} className="me-2" />
            Dashboard
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-primary bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faWrench} className="text-primary" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{stats.total || 0}</h4>
                  <p className="text-muted mb-0">Total Requests</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-warning bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faWrench} className="text-warning" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{stats.pending || 0}</h4>
                  <p className="text-muted mb-0">Pending</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-info bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faWrench} className="text-info" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{stats.inProgress || 0}</h4>
                  <p className="text-muted mb-0">In Progress</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-success bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faWrench} className="text-success" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{stats.completed || 0}</h4>
                  <p className="text-muted mb-0">Completed</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FontAwesomeIcon icon={faSearch} className="me-2" />
                  Search
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by request ID, title, or tenant name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FontAwesomeIcon icon={faFilter} className="me-2" />
                  Filter by Status
                </Form.Label>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Maintenance Requests Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-transparent border-0">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faWrench} className="text-primary me-2" />
            Maintenance Requests ({filteredRequests.length})
          </h5>
        </Card.Header>
        <Card.Body>
          {filteredRequests.length === 0 ? (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faWrench} className="text-muted mb-3" size="3x" />
              <h5 className="text-muted">No maintenance requests found</h5>
              <p className="text-muted">
                {searchTerm || filterStatus !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No maintenance requests have been submitted yet.'
                }
              </p>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Request ID</th>
                  <th>Tenant</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((request) => (
                  <tr key={request.id}>
                    <td>
                      <strong className="text-primary">{request.requestId}</strong>
                    </td>
                    <td>
                      <div>
                        <strong>{request.tenantName}</strong>
                        {request.unitNumber && (
                          <div className="text-muted small">Unit: {request.unitNumber}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong>{request.title}</strong>
                        {request.contactPhone && (
                          <div className="text-muted small">
                            <FontAwesomeIcon icon={faPhone} className="me-1" />
                            {request.contactPhone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <Badge bg="secondary">
                        {maintenanceTypes[request.type] || request.type}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={priorityColors[request.priority]}>
                        {request.priority}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={statusColors[request.status]}>
                        {statusLabels[request.status]}
                      </Badge>
                    </td>
                    <td>
                      <div>
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                        {request.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          as={Link} 
                          to={`/manager/maintenance/${request.id}`} 
                          variant="outline-primary" 
                          size="sm"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                        <Button 
                          variant="outline-warning" 
                          size="sm"
                          onClick={() => handleStatusUpdate(request)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Status Update Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Update Request Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={statusUpdate.status}
                onChange={(e) => setStatusUpdate(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Manager Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={statusUpdate.notes}
                onChange={(e) => setStatusUpdate(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any notes or instructions for the tenant..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmStatusUpdate}>
            Update Status
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MaintenanceRequests; 